import type { TripCashReconciliationView, TripCashSettlementInput } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ApiError } from '../../../shared/shared.api';
import { getTripCashReconciliation, settleTripCash } from '../dispatch.api';

export type TripCashState = { status: 'loading' } | { status: 'loaded'; reconciliation: TripCashReconciliationView } | { status: 'error' };

/** The trip's cash position and recording how it was left. The key is kept after a refusal so a retry replays, and renewed once the server took it. */
export function useTripCash(tripId: string, tripNumber: string) {
  const toast = useToast();
  const [state, setState] = useState<TripCashState>({ status: 'loading' });
  const [readCount, setReadCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    let cancelled = false;
    getTripCashReconciliation(tripId)
      .then((reconciliation) => !cancelled && setState({ status: 'loaded', reconciliation }))
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [tripId, readCount]);

  const settle = useCallback(
    async (input: Omit<TripCashSettlementInput, 'idempotencyKey'>): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await settleTripCash(tripId, { ...input, idempotencyKey });
        toast.success(input.returnedMinor > 0 ? `Cash returned for trip ${tripNumber}` : `Balance carried for trip ${tripNumber}`);
        setIdempotencyKey(crypto.randomUUID());
        setReadCount((count) => count + 1);
        return true;
      } catch (err) {
        setError((err instanceof ApiError ? apiErrorMessage(err) : undefined) ?? 'Could not record the cash. Try again.');
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tripId, tripNumber, idempotencyKey, toast],
  );

  return { state, settle, isSubmitting, error, clearError: () => setError(null) };
}
